import { z } from 'zod';
import { exportPreferenceFields } from './export-preferences.mjs';
import { ARTIFACT_LIMITS, boundedSchema, digest, id, text, unique } from './common.mjs';
import { scalarSchema, valueSlotSchema } from './value.mjs';
import { nativePresentationFields, BOX_SPACING, checkCellPresentation } from './presentation.mjs';

export const cellRefSchema = z.object({ sheetId: id, cellId: id }).strict();
export const formulaExprSchema = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('literal'), value: scalarSchema }).strict(),
    z.object({ kind: z.literal('cell'), ref: cellRefSchema }).strict(),
    z
      .object({
        kind: z.literal('range'),
        sheetId: id,
        cellIds: z.array(id).max(ARTIFACT_LIMITS.cells),
      })
      .strict()
      .superRefine((v, ctx) => unique(v.cellIds, ctx, ['cellIds'])),
    z.object({ kind: z.literal('unary'), op: z.enum(['+', '-']), arg: formulaExprSchema }).strict(),
    z
      .object({
        kind: z.literal('binary'),
        op: z.enum(['+', '-', '*', '/', '=', '<>', '<', '<=', '>', '>=']),
        left: formulaExprSchema,
        right: formulaExprSchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal('call'),
        fn: z.enum(['IF', 'SUM', 'AVERAGE', 'MIN', 'MAX', 'COUNT']),
        args: z.array(formulaExprSchema).min(1).max(100),
      })
      .strict()
      .refine((v) => v.fn !== 'IF' || v.args.length === 3, 'IF requires three arguments'),
  ]),
);
const cacheBase = {
  astDigest: digest,
  inputDigest: digest,
  calculationVersion: id,
  snapshotId: id.nullable(),
  dependencyRefs: z.array(cellRefSchema).max(ARTIFACT_LIMITS.cells),
  rounding: z.enum(['half-even', 'half-up']),
  scale: z.number().int().min(0).max(24),
};
export const formulaCacheSchema = z.discriminatedUnion('status', [
  z.object({ ...cacheBase, status: z.literal('valid'), value: scalarSchema }).strict(),
  z
    .object({
      ...cacheBase,
      status: z.literal('error'),
      code: z.enum(['cycle', 'unsupported', 'missing-input', 'division-by-zero', 'type-error']),
    })
    .strict(),
]);
const cellBase = {
  id,
  rowId: id,
  columnId: id,
  numberFormat: text(100).nullable(),
  ...nativePresentationFields({
    colours: ['text', 'background', 'border'],
    spacing: BOX_SPACING,
    numeric: true,
  }),
};
export const workbookCellSchema = z
  .discriminatedUnion('kind', [
    z.object({ ...cellBase, kind: z.literal('value'), slot: valueSlotSchema }).strict(),
    z
      .object({
        ...cellBase,
        kind: z.literal('formula'),
        ast: formulaExprSchema,
        cache: formulaCacheSchema,
      })
      .strict(),
    z.object({ ...cellBase, kind: z.literal('error'), code: id }).strict(),
  ])
  .superRefine(checkCellPresentation);
export const workbookSheetSchema = z
  .object({
    id,
    title: text(200),
    rowIds: z.array(id).max(ARTIFACT_LIMITS.cells),
    columnIds: z.array(id).max(1000),
    cells: z.array(workbookCellSchema).max(ARTIFACT_LIMITS.cells),
  })
  .strict();

export function formulaReferences(ast, depth = 0) {
  if (depth > ARTIFACT_LIMITS.formulaDepth) throw new TypeError('formula_depth_exceeded');
  switch (ast.kind) {
    case 'cell':
      return [ast.ref];
    case 'range':
      return ast.cellIds.map((cellId) => ({ sheetId: ast.sheetId, cellId }));
    case 'unary':
      return formulaReferences(ast.arg, depth + 1);
    case 'binary':
      return [
        ...formulaReferences(ast.left, depth + 1),
        ...formulaReferences(ast.right, depth + 1),
      ];
    case 'call':
      return ast.args.flatMap((arg) => formulaReferences(arg, depth + 1));
    default:
      return [];
  }
}
function validateFormulaSheets(ast, sheets, depth = 0) {
  if (depth > ARTIFACT_LIMITS.formulaDepth) throw new TypeError('formula_depth_exceeded');
  if (ast.kind === 'range' && !sheets.has(ast.sheetId))
    throw new TypeError('Undeclared formula sheet');
  const children =
    ast.kind === 'call'
      ? ast.args
      : ast.kind === 'binary'
        ? [ast.left, ast.right]
        : ast.kind === 'unary'
          ? [ast.arg]
          : [];
  for (const child of children) validateFormulaSheets(child, sheets, depth + 1);
}
function validateSheet(sheet, ctx) {
  for (const key of ['rowIds', 'columnIds']) unique(sheet[key], ctx, [key]);
  unique(
    sheet.cells.map((c) => c.id),
    ctx,
    ['cells'],
  );
  unique(
    sheet.cells.map((c) => JSON.stringify([c.rowId, c.columnId])),
    ctx,
    ['cells'],
  );
  const rows = new Set(sheet.rowIds),
    columns = new Set(sheet.columnIds);
  if (sheet.cells.some((c) => !rows.has(c.rowId) || !columns.has(c.columnId)))
    ctx.addIssue({ code: 'custom', message: 'Cell outside declared sheet' });
}
function validateWorkbook(value, ctx) {
  unique(
    value.sheets.map((s) => s.id),
    ctx,
    ['sheets'],
  );
  const sheets = new Map(value.sheets.map((s) => [s.id, new Set(s.cells.map((c) => c.id))]));
  if (value.sheets.reduce((n, s) => n + s.cells.length, 0) > ARTIFACT_LIMITS.cells)
    ctx.addIssue({ code: 'custom', message: 'Workbook cell limit exceeded' });
  for (const sheet of value.sheets) {
    validateSheet(sheet, ctx);
    for (const cell of sheet.cells.filter((c) => c.kind === 'formula')) {
      try {
        validateFormulaSheets(cell.ast, sheets);
        const refs = formulaReferences(cell.ast);
        if (refs.some((r) => !sheets.get(r.sheetId)?.has(r.cellId)))
          ctx.addIssue({ code: 'custom', message: 'Undeclared formula reference' });
        const expected = new Set(refs.map((r) => JSON.stringify(r)));
        const cached = cell.cache.dependencyRefs.map((r) => JSON.stringify(r));
        if (
          new Set(cached).size !== cached.length ||
          expected.size !== cached.length ||
          cached.some((r) => !expected.has(r))
        )
          ctx.addIssue({ code: 'custom', message: 'Cache dependencies differ from AST' });
      } catch (error) {
        ctx.addIssue({ code: 'custom', message: error.message });
      }
    }
  }
}
export const workbookPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      sheets: z.array(workbookSheetSchema).min(1).max(ARTIFACT_LIMITS.sheets),
    })
    .strict()
    .superRefine(validateWorkbook),
);
